import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type React from 'react';
import type { Team } from '../../types/team';
import type { DisplayUser, EditUserFormData } from './types';

interface RoleOption {
  value: string;
  label: string;
}

const roleOptions: RoleOption[] = [
  { value: 'user', label: 'User' },
  { value: 'team_lead', label: 'Team Lead' },
  { value: 'client_admin', label: 'Client Admin' },
  { value: 'restricted_editor', label: 'Restricted Editor' },
];

interface UserDetailsFormProps {
  user: DisplayUser;
  formData: EditUserFormData;
  teams: Pick<Team, 'id' | 'teamName' | 'limits'>[];
  canEditUser: boolean;
  canManageRoles: boolean;
  canManageSystem: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectChange: (event: SelectChangeEvent<string>) => void;
}

export const UserDetailsForm: React.FC<UserDetailsFormProps> = ({
  user,
  formData,
  teams,
  canEditUser,
  canManageRoles,
  canManageSystem,
  onChange,
  onSelectChange,
}) => {
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        User Details
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Display Name"
          name="displayName"
          value={formData.displayName}
          onChange={onChange}
          fullWidth
          disabled={!canEditUser}
          inputProps={{ maxLength: 50 }}
          helperText={`${(formData.displayName || '').length}/50`}
          error={(formData.displayName || '').length > 50}
        />
        <TextField label="Email" value={user.email || ''} fullWidth disabled />
        <FormControl fullWidth>
          <InputLabel>Role</InputLabel>
          <Select
            name="role"
            value={formData.role}
            onChange={onSelectChange}
            label="Role"
            disabled={!canManageRoles}
            renderValue={(selected) => {
              const option = roleOptions.find((o) => o.value === selected);
              return option ? option.label : selected;
            }}
          >
            <MenuItem value="client_admin">
              <Box>
                <Typography variant="body1">Client Admin</Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: 'normal', mt: 0.5 }}
                >
                  Full access to client settings, billing, templates, user/team management, and all
                  tenders across the organization.
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem value="team_lead">
              <Box>
                <Typography variant="body1">Team Lead</Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: 'normal', mt: 0.5 }}
                >
                  Can manage team members, upload team templates/library files, and view all tenders
                  assigned to their team.
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem value="user">
              <Box>
                <Typography variant="body1">User</Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: 'normal', mt: 0.5 }}
                >
                  Standard access. Can view the library, create personal tenders, and edit tenders
                  they are explicitly assigned to.
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem value="restricted_editor">
              <Box>
                <Typography variant="body1">Restricted Editor</Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: 'normal', mt: 0.5 }}
                >
                  Limited access. Can only view and edit specific tender sections explicitly
                  assigned to them.
                </Typography>
              </Box>
            </MenuItem>
          </Select>
          <FormHelperText>
            Sets the user's base capabilities. You can override specific permissions below.
          </FormHelperText>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>Team</InputLabel>
          <Select
            name="teamId"
            value={formData.teamId}
            onChange={onSelectChange}
            label="Team"
            disabled={!canEditUser}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {teams.map((team) => (
              <MenuItem key={team.id} value={team.id}>
                {team.teamName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {canManageSystem && (
          <>
            <FormControl fullWidth>
              <InputLabel>Billing Type</InputLabel>
              <Select
                name="billingType"
                value={formData.billingType}
                onChange={onSelectChange}
                label="Billing Type"
              >
                <MenuItem value="organization-managed">Organization-Managed</MenuItem>
                <MenuItem value="self-managed">Self-Managed</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Client ID"
              name="clientId"
              value={formData.clientId || ''}
              onChange={onChange}
              fullWidth
              disabled={!canEditUser}
              inputProps={{ maxLength: 100 }}
              helperText={`${(formData.clientId || '').length}/100`}
              error={(formData.clientId || '').length > 100}
            />
          </>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={formData.disabled}
              onChange={onChange}
              name="disabled"
              color="error"
              disabled={!canEditUser || user.status === 'pending'}
            />
          }
          label="Disable Account"
        />
      </Box>
    </Paper>
  );
};
